import { login } from '#client/constants/routes';
import { DEPLOY_ENV } from '#client/public-env';

/**
 * Generate a Fully Qualified URL
 *
 * @param {string} route
 * @returns {string} url
 */
export const generateZenUrl = (route) => {
  if (!route) {
    return null;
  }
  if (DEPLOY_ENV === 'webpack' || DEPLOY_ENV === 'local') {
    return `http://local.zenbusiness.com:8080/${route.replace(/^\/+/, '')}`;
  }
  return `https://www${DEPLOY_ENV === 'production' ? '.' : '.dev.'}zenbusiness.com/${route.replace(/^\/+/, '')}`;
};

// A bare `/shop/` never reaches shopping-ui: WP-Fastly 301s it to an LLC funnel and drops the
// query string, losing `restoreState`. Always name a flow so the link reaches shopping-ui intact.
const NO_LEAD_FLOW = 'llc-velo-guide';

/**
 * Generate a redirect path to shopping-ui
 * using the lead details to restore the flow state (if available)
 *
 * @param {object} leadDetails - Lead details object
 * @param {string} [leadDetails.flowName] - The flow name to redirect to
 * @param {string} [leadDetails.uuid] - The UUID to restore flow state
 * @returns {string} redirect URL - Falls back to the Velo LLC flow when the lead has no flow name
 */
export const generateShoppingUiRedirectUrl = (leadDetails = {}) => {
  const { flowName, uuid } = leadDetails || {};
  const hasLeadUuid = Boolean(uuid && typeof uuid === 'string');
  const flowPath = flowName || NO_LEAD_FLOW;
  const flowStateIdentifier = hasLeadUuid ? `?restoreState=${uuid}` : '';
  return generateZenUrl(`/shop/${flowPath}${flowStateIdentifier}`);
};

// mfaFactor ('otp' or 'webauthn-roaming') names the factor the Enforce MFA Policy Action enrolls.
export const generateMfaEnrollmentUrl = (redirectTo, mfaFactor) =>
  `${login}?mfa_enroll=true${mfaFactor ? `&mfa_factor=${encodeURIComponent(mfaFactor)}` : ''}&redirectTo=${encodeURIComponent(redirectTo)}`;

/**
 * Reduce a user-supplied redirect target to a same-origin path.
 * The value is parsed with the same URL rules the browser applies at navigation time,
 * and the normalized result is returned, so the checked value and the navigated value match.
 *
 * @param {string} value - Untrusted redirect target
 * @param {string} fallback - Value to return when the target is not a same-origin path
 * @returns {string} normalized pathname, search, and hash, or the fallback
 */
export const toSameOriginPath = (value, fallback) => {
  if (typeof value !== 'string' || !value.startsWith('/')) {
    return fallback;
  }
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) {
      return fallback;
    }
    // Dot segments can collapse to a path that starts with '//', which the browser reads as a host.
    // Return the path only if it resolves back to the URL that was checked.
    const path = url.pathname + url.search + url.hash;
    return new URL(path, window.location.origin).href === url.href ? path : fallback;
  } catch {
    return fallback;
  }
};

/**
 * Login that always challenges MFA: it sets acr_values, and the Auth0 Action then refuses a
 * remembered browser. customers-api needs that fresh challenge before MFA methods are removed.
 *
 * @param {string} redirectTo - Same-origin path to come back to; anything else is dropped
 * @returns {string} login URL
 */
export const generateMfaStepUpUrl = (redirectTo) => {
  const path = toSameOriginPath(redirectTo, null);
  return path ? `${login}?forceMfa=true&redirectTo=${encodeURIComponent(path)}` : `${login}?forceMfa=true`;
};
